export interface NavigationSubItem {
  name: string;
  path: string;
}

export interface NavigationItem {
  name: string;
  path: string;
  icon: string;
  check: string;
  children?: NavigationSubItem[];
}

export interface NavigationSection {
  title: string;
  items: NavigationItem[];
}

// Separate navigation configs for different roles.
// NOTE: item `name`s are load-bearing — the Sidebar's permission/feature
// filters (module view flags, Trade Show, Price Book, Stamp Entry) match on
// them, so sections only regroup existing items; names/paths stay identical.
export const retailerNavigationSections: NavigationSection[] = [
  {
    title: "Dashboard",
    items: [
      {
        name: "Dashboard",
        path: "/retailer/dashboard",
        icon: "dashboard",
        check: "retailer",
      },
    ],
  },
  {
    title: "Orders & Sales",
    items: [
      {
        name: "Order",
        path: "/retailer/order",
        icon: "order",
        check: "retailer",
      },
      // Shown only when GET /retailer/pre-book/available says a pre-book is open (Sidebar filter)
      {
        name: "Pre-Book",
        path: "/retailer/pre-book",
        icon: "product-catalog",
        check: "retailer",
      },
      {
        name: "Price Book",
        path: "/retailer/pricebook",
        icon: "order",
        check: "retailer",
      },
      {
        name: "Order History",
        path: "/retailer/orders/history",
        icon: "order-history",
        check: "retailer",
      },
      {
        name: "Ordered Items",
        path: "/retailer/ordered-items",
        icon: "ordered-items",
        check: "retailer",
      },
      {
        name: "Trade Show",
        path: "/retailer/trade-show",
        icon: "tradeshow-management",
        check: "retailer",
      },
    ],
  },
  {
    title: "Finance",
    items: [
      {
        name: "Account Receivable",
        path: "/retailer/account",
        icon: "account",
        check: "retailer",
      },
    ],
  },
  {
    title: "Catalog & Inventory",
    items: [
      {
        name: "Product Catalog",
        path: "/retailer/product-catalog",
        icon: "product-catalog",
        check: "retailer",
      },
      {
        name: "Product License",
        path: "/retailer/licence",
        icon: "file",
        check: "retailer",
      },
    ],
  },
  {
    title: "Support",
    items: [
      {
        name: "Contact Us",
        path: "/retailer/contact",
        icon: "contactUs",
        check: "retailer",
      },
      {
        name: "Chat",
        path: "/retailer/chat",
        icon: "chat",
        check: "retailer",
      },
    ],
  },
  {
    title: "Settings",
    items: [
      {
        name: "Policies",
        path: "/retailer/policies",
        icon: "policies",
        check: "retailer",
      },
    ],
  },
];

export const retailerNavigationConfig: NavigationItem[] = retailerNavigationSections.flatMap(
  (section) => section.items
);

// Admin sidebar grouped into titled sections. The Dashboard item expands into
// sub-links that deep-link to the DashboardV1 tabs (?tab=<key> matches the
// TABS keys in pages/admin/dashboard/DashboardV1/index.tsx).
export const adminNavigationSections: NavigationSection[] = [
  {
    title: "Dashboard",
    items: [
      {
        name: "Dashboard",
        path: "/admin/dashboard-v1",
        icon: "dashboard",
        check: "distributor",
        children: [
          { name: "Orders", path: "/admin/dashboard-v1?tab=orders" },
          { name: "Sales", path: "/admin/dashboard-v1?tab=sales-analytics" },
          { name: "Purchase", path: "/admin/dashboard-v1?tab=purchase" },
          { name: "Inventory", path: "/admin/dashboard-v1?tab=inventory" },
          { name: "Customers", path: "/admin/dashboard-v1?tab=customers" },
          { name: "Payments", path: "/admin/dashboard-v1?tab=payments" },
          { name: "E-Pick", path: "/admin/dashboard-v1?tab=epick" },
        ],
      },
    ],
  },
  {
    title: "Catalog & Inventory",
    items: [
      {
        name: "Products",
        path: "/admin/product-v1",
        icon: "inventory",
        check: "distributor",
      },
      {
        name: "Pre-Book",
        path: "/admin/pre-book",
        icon: "product-catalog",
        check: "distributor",
      },
    ],
  },
  {
    title: "Retail Partners",
    items: [
      {
        name: "Customer",
        path: "/admin/customer-v1",
        icon: "customers",
        check: "distributor",
      },
      {
        name: "Customer Requests",
        path: "/admin/retailer-requests",
        icon: "retailer-requests",
        check: "distributor",
      },
    ],
  },
  {
    title: "Suppliers",
    items: [
      {
        name: "Vendors",
        path: "/admin/vendor-v1",
        icon: "vendors",
        check: "distributor",
      },
      {
        name: "Account Payable",
        path: "/admin/account-payable",
        icon: "account-payable",
        check: "distributor",
      },
      {
        name: "Create PO",
        path: "/admin/create-po",
        icon: "create-po",
        check: "distributor",
      },
    ],
  },
  {
    title: "Orders & Sales",
    items: [
      {
        name: "Orders",
        path: "/admin/order",
        icon: "orders",
        check: "distributor",
      },
      {
        name: "Order Entry",
        path: "/admin/order-entry",
        icon: "order",
        check: "distributor",
      },
      // {
      //   name: "Calendar",
      //   path: "/admin/calender",
      //   icon: "CalendarMonthIcon",
      //   check: "distributor",
      // },
      {
        name: "Call List",
        path: "/admin/call-list",
        icon: "call-list",
        check: "distributor",
      },
    ],
  },
  {
    title: "Finance",
    items: [
      // {
      //   name: "Account Receivable",
      //   path: "/admin/account",
      //   icon: "account",
      //   check: "distributor",
      // },
      {
        name: "AR Entry",
        path: "/admin/ar-entry",
        icon: "ar-entry",
        check: "distributor",
      },
      // {
      //   name: "Stamp Entry",
      //   path: "/admin/stamp-entry",
      //   icon: "stamp-entry",
      //   check: "distributor",
      // },
    ],
  },
  {
    title: "Marketing",
    items: [
      {
        name: "Marketing",
        path: "/admin/promo",
        icon: "promo",
        check: "distributor",
        children: [
          { name: "Promo & Marketing", path: "/admin/promo" },
          { name: "Tradeshow Management", path: "/admin/tradeshow-management" },
          { name: "Notifications", path: "/admin/notifications" },
        ],
      },
    ],
  },
  {
    title: "Support",
    items: [
      {
        name: "Support Tickets",
        path: "/admin/support-tickets",
        icon: "SupportTicketsIcon",
        check: "distributor",
      },
      {
        name: "Chat",
        path: "/admin/chat",
        icon: "chat",
        check: "distributor",
      },
    ],
  },
  {
    title: "Reports & Analytics",
    items: [
      {
        name: "Reports & Analytics",
        path: "/admin/reports-analytics",
        icon: "reports-analytics",
        check: "distributor",
      },
      {
        name: "MSA Generate",
        path: "/admin/msa",
        icon: "msa",
        check: "distributor",
      },
      {
        name: "Update (End-of-Day)",
        path: "/admin/day-close",
        icon: "day-close",
        check: "distributor",
      },
    ],
  },
  {
    title: "Administration",
    items: [
      {
        name: "User Management",
        path: "/admin/permissions",
        icon: "user-management",
        check: "distributor",
      },
      {
        name: "Track Login Devices",
        path: "/admin/track-login-devices",
        icon: "track-login-devices",
        check: "distributor",
      },
    ],
  },
  {
    title: "Settings",
    items: [
      {
        name: "Settings",
        path: "/admin/settings",
        icon: "settings",
        check: "distributor",
      },
      {
        name: "Setup / Definitions",
        path: "/admin/setup-v1",
        icon: "setup-definitions",
        check: "distributor",
      },
      {
        name: "Policies",
        path: "/admin/policies",
        icon: "policies",
        check: "distributor",
      },
    ],
  },
  {
    title: "Fulfillment",
    items: [
      {
        name: "E-Pick",
        path: "/admin/e-pick-settings",
        icon: "e-pick-settings",
        check: "distributor",
      },
      {
        name: "Driver Management",
        path: "/admin/driver-management",
        icon: "driver-management",
        check: "distributor",
      },
      // {
      //   name: "Receivable Settings",
      //   path: "/admin/receivable-settings",
      //   icon: "account",
      //   check: "distributor",
      // },
    ],
  },
];

// Flat list derived from the sections — keeps getNavigationConfig() and the
// horizontal sidebar variant working unchanged.
export const adminNavigationConfig: NavigationItem[] = adminNavigationSections.flatMap(
  (section) => section.items
);

// Sales sidebar sections. Item names MUST stay exactly as the permission
// module names ("Generate MSA", "Update (End-of-Day)", "Track Login Device",
// etc.) — the Sidebar keeps items only when the matching module has view=true.
export const salesNavigationSections: NavigationSection[] = [
  {
    title: "Dashboard",
    items: [
      {
        name: "Dashboard",
        path: "/sales/dashboard",
        icon: "dashboard",
        check: "sales",
      },
    ],
  },
  {
    title: "Orders & Sales",
    items: [
      {
        name: "Orders",
        path: "/sales/order",
        icon: "order",
        check: "sales",
      },
      // Shown only when GET /sales/pre-book/available says a pre-book is open to the
      // customer selected in the sales session (Sidebar filter)
      {
        name: "Pre-Book",
        path: "/sales/pre-book",
        icon: "product-catalog",
        check: "sales",
      },
      {
        name: "Price Book",
        path: "/sales/pricebook",
        icon: "order",
        check: "sales",
      },
      {
        name: "Price Book Cart",
        path: "/sales/pricebook/cart",
        icon: "order",
        check: "sales",
      },
      {
        name: "Trade Show",
        path: "/sales/trade-show",
        icon: "tradeshow-management",
        check: "sales",
      },
      {
        name: "Return Orders",
        path: "/sales/return-order",
        icon: "order",
        check: "sales",
      },
      {
        name: "Order Confirmation",
        path: "/sales/order-confirmation",
        icon: "order",
        check: "sales",
      },
      {
        name: "Ordered Items",
        path: "/sales/ordered-items",
        icon: "ordered-items",
        check: "sales",
      },
      {
        name: "Order History",
        path: "/sales/orders/history",
        icon: "order-history",
        check: "sales",
      },
      {
        name: "Order Checker",
        path: "/sales/order-checker",
        icon: "order",
        check: "sales",
      },
      {
        name: "Order Entry",
        path: "/sales/order-entry",
        icon: "order",
        check: "sales",
      },
      {
        name: "Calendar",
        path: "/sales/calender",
        icon: "CalendarMonthIcon",
        check: "sales",
      },
      {
        name: "Call List",
        path: "/sales/call-list",
        icon: "call-list",
        check: "sales",
      },
    ],
  },
  {
    title: "Retail Partners",
    items: [
      {
        name: "Retailers",
        path: "/sales/retailers",
        icon: "customers",
        check: "sales",
      },
    ],
  },
  {
    title: "Catalog & Inventory",
    items: [
      {
        name: "Product",
        path: "/sales/product",
        icon: "products",
        check: "sales",
      },
      // Admin pre-book screens (create / edit / delete, approve orders). Not "Pre-Book": that
      // name is the ordering screen above and the Sidebar matches items to modules by name.
      {
        name: "Pre-Book Management",
        path: "/sales/pre-book-management",
        icon: "product-catalog",
        check: "sales",
      },
    ],
  },
  {
    title: "Suppliers",
    items: [
      {
        name: "Vendor",
        path: "/sales/vendor",
        icon: "vendors",
        check: "sales",
      },
      {
        name: "Account Payable",
        path: "/sales/account-payable",
        icon: "account-payable",
        check: "sales",
      },
      {
        name: "Create PO",
        path: "/sales/create-po",
        icon: "create-po",
        check: "sales",
      },
    ],
  },
  {
    title: "Finance",
    items: [
      {
        name: "Account Receivable",
        path: "/sales/account",
        icon: "account",
        check: "sales",
      },
      {
        name: "AR Entry",
        path: "/sales/ar-entry",
        icon: "ar-entry",
        check: "sales",
      },
      // {
      //   name: "Stamp Entry",
      //   path: "/sales/stamp-entry",
      //   icon: "stamp-entry",
      //   check: "sales",
      // },
    ],
  },
  {
    title: "Marketing",
    items: [
      {
        name: "Promo & marketing",
        path: "/sales/promo",
        icon: "promo",
        check: "sales",
      },
      {
        name: "Notifications",
        path: "/sales/notifications",
        icon: "notifications",
        check: "sales",
      },
    ],
  },
  {
    title: "Reports & Analytics",
    items: [
      {
        name: "Reports & Analytics",
        path: "/sales/reports-analytics",
        icon: "reports-analytics",
        check: "sales",
      },
      {
        name: "Generate MSA",
        path: "/sales/msa",
        icon: "msa",
        check: "sales",
      },
      {
        name: "Update (End-of-Day)",
        path: "/sales/day-close",
        icon: "day-close",
        check: "sales",
      },
    ],
  },
  {
    title: "Administration",
    items: [
      {
        name: "Track Login Device",
        path: "/sales/track-login-devices",
        icon: "track-login-devices",
        check: "sales",
      },
    ],
  },
  {
    title: "Fulfillment",
    items: [
      {
        name: "Epick",
        path: "/sales/epick",
        icon: "e-pick-settings",
        check: "sales",
      },
      {
        name: "Driver Management",
        path: "/sales/driver-management",
        icon: "driver-management",
        check: "sales",
      },
    ],
  },
  {
    title: "Support",
    items: [
      {
        name: "Chat",
        path: "/sales/chat",
        icon: "chat",
        check: "sales",
      },
    ],
  },
  {
    title: "Settings",
    items: [
      {
        name: "Setup / Definitions",
        path: "/sales/setup-v1",
        icon: "setup-definitions",
        check: "sales",
      },
      {
        name: "Policies",
        path: "/sales/policies",
        icon: "policies",
        check: "sales",
      },
    ],
  },
];

export const salesNavigationConfig: NavigationItem[] = salesNavigationSections.flatMap(
  (section) => section.items
);

// Function to get navigation config based on user role
export const getNavigationConfig = (role: string | null): NavigationItem[] => {
  switch (role) {
    case 'distributor':
      return adminNavigationConfig;
    case 'retailer':
      return retailerNavigationConfig;
    case 'sales':
      return salesNavigationConfig;
    default:
      return retailerNavigationConfig; // Default fallback
  }
};

// Legacy export for backward compatibility
export const navigationConfig: NavigationItem[] = [
  ...retailerNavigationConfig,
  ...adminNavigationConfig,
  ...salesNavigationConfig,
];

export const isParentActive = (currentPath: string, itemPath: string): boolean => {
  if (currentPath === itemPath) return true;
  return currentPath.startsWith(itemPath + '/');
}; 